<!-- Performance Overview - KPIs avec tendances -->
<div class="space-y-6">
  <!-- Sélecteur de période -->
  <div class="flex items-center justify-between">
    <h2 class="text-xl font-bold text-gray-800 dark:text-gray-100">📊 Performance</h2>
    <div class="flex gap-2 bg-gray-100 dark:bg-slate-800 rounded-xl p-1">
      @for (period of periods; track period) {
        <button
          (click)="selectPeriod(period.value)"
        [ngClass]="
          currentPeriod === period.value
            ? 'bg-white dark:bg-slate-800 text-indigo-600 shadow-sm dark:shadow-slate-900/30'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:text-gray-100'
        "
          class="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
          >
          {{ period.label }}
        </button>
      }
    </div>
  </div>

  <!-- Loading State -->
  @if (isLoading) {
    <div
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
      >
      @for (i of [1, 2, 3, 4]; track i) {
        <div
          class="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm dark:shadow-slate-900/30 animate-pulse"
          >
          <div class="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/2 mb-4"></div>
          <div class="h-8 bg-gray-200 dark:bg-slate-700 rounded w-3/4 mb-2"></div>
          <div class="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/3"></div>
        </div>
      }
    </div>
  }

  <!-- Error State -->
  @if (error && !isLoading) {
    <div
      class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-6 text-center"
      >
      <span class="text-3xl mb-2 block">⚠️</span>
      <p class="text-red-600 dark:text-red-400 font-medium">{{ error }}</p>
      <button
        (click)="selectPeriod(currentPeriod)"
        class="mt-4 px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 transition"
        >
        Réessayer
      </button>
    </div>
  }

  <!-- KPIs Cards -->
  @if (performanceData && !isLoading) {
    <div
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
      >
      <!-- Chiffre d'affaires -->
      <div
        class="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm dark:shadow-slate-900/30 border border-gray-100 dark:border-slate-700 hover:shadow-md dark:shadow-slate-900/50 transition-shadow"
        >
        <div class="flex items-center justify-between mb-4">
          <span class="text-gray-500 dark:text-gray-400 text-sm font-medium"
            >Chiffre d'affaires</span
            >
            <span class="text-2xl">💰</span>
          </div>
          <div class="flex items-end justify-between">
            <div>
              <p class="text-3xl font-bold text-gray-900 dark:text-white">
                {{ formatCurrency(performanceData.revenue.current) }}
              </p>
              <div
                class="flex items-center gap-1 mt-2 px-2 py-1 rounded-lg w-fit"
                [ngClass]="getTrendBgClass(performanceData.revenue.evolution.trend)"
                >
                <span>{{
                  getTrendIcon(performanceData.revenue.evolution.trend)
                }}</span>
                <span
                  class="text-sm font-medium"
                  [ngClass]="getTrendClass(performanceData.revenue.evolution.trend)"
                  >
                  {{
                  formatPercentage(performanceData.revenue.evolution.percentage)
                  }}
                </span>
                <span class="text-xs text-gray-500 dark:text-gray-400">{{
                  getComparisonLabel()
                }}</span>
              </div>
            </div>
            <!-- Sparkline -->
            <svg width="120" height="40" class="opacity-80" aria-hidden="true">
              <defs>
                <linearGradient
                  id="sparklineGradientUp"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                  >
                  <stop offset="0%" stop-color="#10b981" stop-opacity="0.8" />
                  <stop offset="100%" stop-color="#10b981" stop-opacity="0.1" />
                </linearGradient>
                <linearGradient
                  id="sparklineGradientDown"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                  >
                  <stop offset="0%" stop-color="#ef4444" stop-opacity="0.8" />
                  <stop offset="100%" stop-color="#ef4444" stop-opacity="0.1" />
                </linearGradient>
                <linearGradient
                  id="sparklineGradientStable"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                  >
                  <stop offset="0%" stop-color="#6b7280" stop-opacity="0.8" />
                  <stop offset="100%" stop-color="#6b7280" stop-opacity="0.1" />
                </linearGradient>
              </defs>
              <path
                [attr.d]="getSparklinePath()"
                fill="none"
            [attr.stroke]="
              performanceData.revenue.evolution.trend === 'up'
                ? '#10b981'
                : performanceData.revenue.evolution.trend === 'down'
                  ? '#ef4444'
                  : '#6b7280'
            "
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                />
              </svg>
            </div>
          </div>
          <!-- Transactions -->
          <div
            class="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm dark:shadow-slate-900/30 border border-gray-100 dark:border-slate-700 hover:shadow-md dark:shadow-slate-900/50 transition-shadow"
            >
            <div class="flex items-center justify-between mb-4">
              <span class="text-gray-500 dark:text-gray-400 text-sm font-medium">Transactions</span>
              <span class="text-2xl">🧾</span>
            </div>
            <div>
              <p class="text-3xl font-bold text-gray-900 dark:text-white">
                {{ performanceData.transactions.current }}
              </p>
              <div
                class="flex items-center gap-1 mt-2 px-2 py-1 rounded-lg w-fit"
          [ngClass]="
            getTrendBgClass(performanceData.transactions.evolution.trend)
          "
                >
                <span>{{
                  getTrendIcon(performanceData.transactions.evolution.trend)
                }}</span>
                <span
                  class="text-sm font-medium"
            [ngClass]="
              getTrendClass(performanceData.transactions.evolution.trend)
            "
                  >
                  {{
                  formatPercentage(
                  performanceData.transactions.evolution.percentage
                  )
                  }}
                </span>
                <span class="text-xs text-gray-500 dark:text-gray-400">{{ getComparisonLabel() }}</span>
              </div>
            </div>
          </div>
          <!-- Panier moyen -->
          <div
            class="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm dark:shadow-slate-900/30 border border-gray-100 dark:border-slate-700 hover:shadow-md dark:shadow-slate-900/50 transition-shadow"
            >
            <div class="flex items-center justify-between mb-4">
              <span class="text-gray-500 dark:text-gray-400 text-sm font-medium">Panier moyen</span>
              <span class="text-2xl">🛒</span>
            </div>
            <div>
              <p class="text-3xl font-bold text-gray-900 dark:text-white">
                {{ formatCurrency(performanceData.avgBasket.current) }}
              </p>
              <div
                class="flex items-center gap-1 mt-2 px-2 py-1 rounded-lg w-fit"
                [ngClass]="getTrendBgClass(performanceData.avgBasket.evolution.trend)"
                >
                <span>{{
                  getTrendIcon(performanceData.avgBasket.evolution.trend)
                }}</span>
                <span
                  class="text-sm font-medium"
                  [ngClass]="getTrendClass(performanceData.avgBasket.evolution.trend)"
                  >
                  {{
                  formatPercentage(performanceData.avgBasket.evolution.percentage)
                  }}
                </span>
                <span class="text-xs text-gray-500 dark:text-gray-400">{{ getComparisonLabel() }}</span>
              </div>
            </div>
          </div>
          <!-- Articles vendus -->
          <div
            class="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm dark:shadow-slate-900/30 border border-gray-100 dark:border-slate-700 hover:shadow-md dark:shadow-slate-900/50 transition-shadow"
            >
            <div class="flex items-center justify-between mb-4">
              <span class="text-gray-500 dark:text-gray-400 text-sm font-medium">Articles vendus</span>
              <span class="text-2xl">📦</span>
            </div>
            <div>
              <p class="text-3xl font-bold text-gray-900 dark:text-white">
                {{ performanceData.itemsSold.current }}
              </p>
              <div
                class="flex items-center gap-1 mt-2 px-2 py-1 rounded-lg w-fit"
                [ngClass]="getTrendBgClass(performanceData.itemsSold.evolution.trend)"
                >
                <span>{{
                  getTrendIcon(performanceData.itemsSold.evolution.trend)
                }}</span>
                <span
                  class="text-sm font-medium"
                  [ngClass]="getTrendClass(performanceData.itemsSold.evolution.trend)"
                  >
                  {{
                  formatPercentage(performanceData.itemsSold.evolution.percentage)
                  }}
                </span>
                <span class="text-xs text-gray-500 dark:text-gray-400">{{ getComparisonLabel() }}</span>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
